import { useState, useEffect } from 'react';
import { Container, Card, Button, Alert, Spinner } from 'react-bootstrap';
import { useParams, useNavigate } from 'react-router-dom';
import { getPlansWithStatus, buySlot } from '../../api/planApi';
import { getMyWallets } from '../../api/walletApi';
import { useAuth } from '../../context/AuthContext';

const cyan = '#00e5ff';
const cardDark = {
  background: 'radial-gradient(ellipse at 100% 0%, rgba(0,229,255,0.08) 0%, #0d0d0d 65%)',
  border: '1px solid rgba(0,229,255,0.2)',
  color: '#fff',
};

const InfoRow = ({ label, value, valueStyle }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 0', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
    <span style={{ color: '#888', fontSize: 13 }}>{label}</span>
    <span style={{ fontWeight: 600, fontSize: 15, ...valueStyle }}>{value}</span>
  </div>
);

const WALLETS = [
  { key: 'main_wallet', label: 'Main Wallet' },
  { key: 'one_wallet',  label: 'One Wallet'  },
];

const BuySlot = () => {
  const { planId } = useParams();
  const navigate = useNavigate();
  const { updateUser } = useAuth();
  const [plan, setPlan] = useState(null);
  const [walletBalances, setWalletBalances] = useState({ main_wallet: 0, one_wallet: 0 });
  const [selectedWallet, setSelectedWallet] = useState('main_wallet');
  const [loading, setLoading] = useState(true);
  const [buying, setBuying] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    Promise.all([getPlansWithStatus(), getMyWallets()])
      .then(([plansRes, walletsRes]) => {
        const found = plansRes.data.plans.find((p) => p._id === planId);
        if (!found) { navigate('/slots'); return; }
        setPlan(found);
        const w = walletsRes.data.wallets;
        setWalletBalances({
          main_wallet: w.main_wallet?.balance ?? 0,
          one_wallet:  w.one_wallet?.balance  ?? 0,
        });
      })
      .finally(() => setLoading(false));
  }, [planId, navigate]);

  const selectedBalance = walletBalances[selectedWallet];
  const insufficient = plan && selectedBalance < plan.amount;

  const handleBuy = async () => {
    setError('');
    setBuying(true);
    try {
      const res = await buySlot(planId, selectedWallet);
      if (res.data.success) {
        setSuccess(true);
        setTimeout(() => navigate('/slots'), 2000);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Purchase failed');
    } finally {
      setBuying(false);
    }
  };

  if (loading) return (
    <Container className="mt-5 text-center">
      <Spinner animation="border" style={{ color: cyan }} />
    </Container>
  );

  return (
    <div style={{ minHeight: '100vh', background: '#0a0a0a', paddingBottom: 40 }}>
      <Container className="pt-4" style={{ maxWidth: 480 }}>
        <button
          onClick={() => navigate('/slots')}
          style={{ background: 'none', border: 'none', color: cyan, fontSize: 13, cursor: 'pointer', marginBottom: 20, padding: 0 }}
        >
          ‹ Back to Slots
        </button>

        <h5 style={{ color: '#fff', fontWeight: 700, marginBottom: 20 }}>Confirm Purchase</h5>

        {success ? (
          <Card style={cardDark} className="p-4 text-center">
            <div style={{ fontSize: 40, marginBottom: 12 }}>✓</div>
            <div style={{ color: '#66ff99', fontWeight: 700, fontSize: 16 }}>Slot Upgraded Successfully!</div>
            <div style={{ color: '#888', fontSize: 13, marginTop: 8 }}>Redirecting to slots...</div>
          </Card>
        ) : (
          <Card style={cardDark} className="p-4">
            <InfoRow label="Slot Name"   value={plan?.planName} valueStyle={{ color: '#fff' }} />
            <InfoRow label="Amount"      value={`${plan?.amount?.toFixed(2)} USDT`} valueStyle={{ color: '#66ff99' }} />

            {/* Wallet selector */}
            <div style={{ padding: '14px 0', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
              <div style={{ color: '#888', fontSize: 13, marginBottom: 10 }}>Pay from</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                {WALLETS.map(({ key, label }) => {
                  const bal = walletBalances[key];
                  const active = selectedWallet === key;
                  const low = bal < (plan?.amount ?? 0);
                  return (
                    <div
                      key={key}
                      onClick={() => setSelectedWallet(key)}
                      style={{
                        border: active ? `1.5px solid ${cyan}` : '1.5px solid rgba(255,255,255,0.1)',
                        borderRadius: 8,
                        padding: '10px 12px',
                        cursor: 'pointer',
                        background: active ? 'rgba(0,229,255,0.07)' : 'rgba(255,255,255,0.02)',
                        transition: 'all 0.15s',
                      }}
                    >
                      <div style={{ fontSize: 12, color: active ? cyan : '#888', fontWeight: 600, marginBottom: 4 }}>{label}</div>
                      <div style={{ fontSize: 15, fontWeight: 700, color: low ? '#ff6666' : '#fff' }}>
                        {Number(bal).toFixed(2)} USDT
                      </div>
                      {low && <div style={{ fontSize: 10, color: '#ff6666', marginTop: 2 }}>Insufficient</div>}
                    </div>
                  );
                })}
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 0' }}>
              <span style={{ color: '#fff', fontWeight: 700, fontSize: 14 }}>Total Payable</span>
              <span style={{ fontWeight: 800, fontSize: 18, color: '#66ff99' }}>{plan?.amount?.toFixed(2)} USDT</span>
            </div>

            {insufficient && (
              <Alert variant="danger" style={{ background: 'rgba(200,50,50,0.15)', border: '1px solid rgba(200,50,50,0.3)', color: '#ff6666', fontSize: 13 }}>
                Insufficient balance in selected wallet. You need <strong>{plan.amount?.toFixed(2)} USDT</strong> but have <strong>{Number(selectedBalance).toFixed(2)} USDT</strong>.
              </Alert>
            )}
            {plan?.purchased && (
              <Alert variant="warning" style={{ fontSize: 13 }}>
                You have already purchased this slot.
              </Alert>
            )}
            {error && (
              <Alert variant="danger" style={{ background: 'rgba(200,50,50,0.15)', border: '1px solid rgba(200,50,50,0.3)', color: '#ff6666', fontSize: 13 }}>
                {error}
              </Alert>
            )}

            <Button
              onClick={handleBuy}
              disabled={buying || insufficient || plan?.purchased}
              style={{
                background: insufficient || plan?.purchased ? 'rgba(255,255,255,0.05)' : cyan,
                border: 'none',
                color: insufficient || plan?.purchased ? '#555' : '#000',
                fontWeight: 700,
                fontSize: 15,
                padding: '12px',
                borderRadius: 8,
                marginTop: 8,
                width: '100%',
              }}
            >
              {buying ? <Spinner animation="border" size="sm" /> : 'Upgrade Slot'}
            </Button>
          </Card>
        )}
      </Container>
    </div>
  );
};

export default BuySlot;
